import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, PencilSimple, Trash, UploadSimple, ToggleLeft, ToggleRight, X as XIcon, Lock, ImageSquare } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = { id: '', label: '', fields: '', is_active: true };

const PaymentMethodTypesManager = ({ onTypesChanged }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create, string = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const iconInputRef = useRef(null);
  const [iconTargetId, setIconTargetId] = useState(null);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/direct-payments/method-types`);
      setTypes(r.data?.types || []);
    } catch (e) { toast.error('Failed to load method types'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowDialog(true);
  };
  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      id: t.id,
      label: t.label,
      fields: (t.fields || []).join(', '),
      is_active: t.is_active !== false,
    });
    setShowDialog(true);
  };
  const closeDialog = () => { setShowDialog(false); setForm(EMPTY_FORM); setEditingId(null); };

  const save = async () => {
    const fields = form.fields.split(',').map(s => s.trim()).filter(Boolean);
    if (!editingId && !form.id.trim()) { toast.error('ID is required'); return; }
    if (!form.label.trim()) { toast.error('Label is required'); return; }
    if (fields.length === 0) { toast.error('At least one field is required'); return; }
    setBusy(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/direct-payments/method-types/${editingId}`, {
          label: form.label.trim(), fields, is_active: form.is_active,
        });
        toast.success('Method type updated');
      } else {
        await axios.post(`${API_URL}/api/admin/direct-payments/method-types`, {
          id: form.id.trim().toLowerCase(),
          label: form.label.trim(),
          fields, is_active: form.is_active,
        });
        toast.success('Method type added');
      }
      closeDialog();
      await fetchTypes();
      onTypesChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const toggleActive = async (t) => {
    try {
      await axios.put(`${API_URL}/api/admin/direct-payments/method-types/${t.id}`, { is_active: !t.is_active });
      await fetchTypes();
      onTypesChanged?.();
    } catch (e) { toast.error('Toggle failed'); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete payment method "${t.label}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/direct-payments/method-types/${t.id}`);
      toast.success('Deleted');
      await fetchTypes();
      onTypesChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const triggerIconUpload = (id) => {
    setIconTargetId(id);
    iconInputRef.current?.click();
  };
  const handleIconFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !iconTargetId) return;
    if (file.size > 500 * 1024) { toast.error('Icon too large. Max 500KB'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API_URL}/api/admin/direct-payments/method-types/${iconTargetId}/icon`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Icon uploaded');
      await fetchTypes();
      onTypesChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Icon upload failed'); }
    finally {
      setBusy(false);
      if (iconInputRef.current) iconInputRef.current.value = '';
      setIconTargetId(null);
    }
  };

  return (
    <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-4 mb-4" data-testid="payment-method-types-manager">
      <input ref={iconInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleIconFile} data-testid="hidden-icon-input" />

      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <h3 className="text-white text-sm font-semibold">Payment Method Types Registry</h3>
          <p className="text-[11px] text-[#6b7a99] mt-0.5">Add custom payment methods, edit labels/fields, upload icons, and activate or deactivate any type globally. Built-in methods can be edited and deactivated but not deleted.</p>
        </div>
        <Button size="sm" onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 h-8 rounded-lg text-xs" data-testid="add-payment-type-btn">
          <Plus size={14} className="mr-1" /> Add Method Type
        </Button>
      </div>

      {loading ? (
        <div className="text-[#6b7a99] text-sm py-4 text-center">Loading…</div>
      ) : (
        <div className="space-y-1.5">
          {types.map(t => (
            <div key={t.id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${t.is_active ? 'border-[#252d3d] bg-[#0f1320]' : 'border-amber-700/30 bg-amber-900/10 opacity-80'}`} data-testid={`pmt-row-${t.id}`}>
              {/* Icon */}
              <div className="w-9 h-9 rounded-md bg-[#1a2030] border border-[#252d3d] flex items-center justify-center overflow-hidden shrink-0">
                {t.icon_url ? (
                  <img src={`${API_URL}${t.icon_url}`} alt={t.label} className="w-full h-full object-contain" data-testid={`pmt-icon-${t.id}`} />
                ) : (
                  <ImageSquare size={16} className="text-[#6b7a99]" />
                )}
              </div>
              {/* Identity */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-white text-sm font-semibold truncate">{t.label}</span>
                  {t.is_builtin && (
                    <span className="text-[9px] uppercase tracking-wider bg-indigo-500/20 text-indigo-300 border border-indigo-400/30 px-1.5 py-0.5 rounded-full flex items-center gap-0.5" title="Built-in — cannot be deleted">
                      <Lock size={9} weight="fill" /> built-in
                    </span>
                  )}
                  {!t.is_active && (
                    <span className="text-[9px] uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-400/30 px-1.5 py-0.5 rounded-full">Inactive</span>
                  )}
                </div>
                <div className="text-[10px] text-[#6b7a99] truncate font-mono">
                  <span className="text-[#8896b0]">{t.id}</span> · fields: {t.fields?.join(', ') || '—'}
                </div>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => triggerIconUpload(t.id)} className="text-[#8896b0] hover:text-white p-1.5" title="Upload icon" data-testid={`pmt-upload-icon-${t.id}`}>
                  <UploadSimple size={14} />
                </button>
                <button onClick={() => openEdit(t)} className="text-[#8896b0] hover:text-white p-1.5" title="Edit" data-testid={`pmt-edit-${t.id}`}>
                  <PencilSimple size={14} />
                </button>
                <button onClick={() => toggleActive(t)} className="text-[#8896b0] hover:text-white p-1.5" title={t.is_active ? 'Deactivate' : 'Activate'} data-testid={`pmt-toggle-${t.id}`}>
                  {t.is_active ? <ToggleRight size={18} weight="fill" className="text-emerald-400" /> : <ToggleLeft size={18} />}
                </button>
                <button onClick={() => remove(t)} disabled={t.is_builtin}
                  className={`p-1.5 ${t.is_builtin ? 'text-[#3b4458] cursor-not-allowed' : 'text-red-400 hover:text-red-300'}`}
                  title={t.is_builtin ? 'Built-in — cannot delete' : 'Delete'} data-testid={`pmt-delete-${t.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
          {types.length === 0 && (
            <div className="text-[#6b7a99] text-sm py-4 text-center">No method types yet — click <strong>Add Method Type</strong>.</div>
          )}
        </div>
      )}

      {/* Add/Edit modal */}
      {showDialog && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pmt-dialog">
          <div className="bg-[#161b26] border border-[#252d3d] rounded-2xl p-5 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white text-base font-semibold">{editingId ? `Edit "${editingId}"` : 'Add Payment Method Type'}</h4>
              <button onClick={closeDialog} className="text-[#6b7a99] hover:text-white"><XIcon size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-[#6b7a99] uppercase tracking-wider">ID (machine name)</label>
                <Input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })}
                  placeholder="e.g. googlepay, applepay, cashfree" disabled={!!editingId}
                  className="h-9 text-sm mt-1 bg-[#0f1320] border-[#252d3d] text-white" data-testid="pmt-id-input" />
                <p className="text-[10px] text-[#6b7a99] mt-1">Alphanumeric + underscores/dashes only. Cannot be changed after creation.</p>
              </div>
              <div>
                <label className="text-[10px] text-[#6b7a99] uppercase tracking-wider">Display Label</label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g. Google Pay" className="h-9 text-sm mt-1 bg-[#0f1320] border-[#252d3d] text-white" data-testid="pmt-label-input" />
              </div>
              <div>
                <label className="text-[10px] text-[#6b7a99] uppercase tracking-wider">Fields (comma-separated)</label>
                <Input value={form.fields} onChange={(e) => setForm({ ...form, fields: e.target.value })}
                  placeholder="e.g. gpay_number, qr_image_url" className="h-9 text-sm mt-1 bg-[#0f1320] border-[#252d3d] text-white" data-testid="pmt-fields-input" />
                <p className="text-[10px] text-[#6b7a99] mt-1">The fields sellers will fill when they choose this method. Use snake_case.</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4 accent-emerald-500" data-testid="pmt-active-checkbox" />
                Active (sellers can use this method)
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button size="sm" variant="ghost" onClick={closeDialog} className="text-[#8896b0] hover:text-white h-9 rounded-lg text-xs">Cancel</Button>
              <Button size="sm" onClick={save} disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 h-9 rounded-lg text-xs" data-testid="pmt-save-btn">
                {editingId ? 'Save Changes' : 'Add Method'}
              </Button>
            </div>
            {editingId && (
              <p className="text-[10px] text-[#6b7a99] mt-3 leading-snug">
                Tip: after saving, use the <UploadSimple size={10} className="inline" /> button in the list to upload an icon for this method.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodTypesManager;
